import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import { QuestionSummary } from '../../model/question-summary'

const Stack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
`

const Figures = styled.dl`
	display: flex;
	flex-direction: column;
	gap: 40px;
	margin: 0;
`

const Caption = styled.dt`
	color: ${colors.blackish};
	font-size: 20px;
	font-weight: 700;
	line-height: 1.2;
	margin: 0 0 8px;

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

const Value = styled.dd`
	align-items: baseline;
	color: ${colors.blackish};
	display: flex;
	flex-wrap: wrap;
	font-size: 64px;
	font-weight: 700;
	gap: 8px 24px;
	line-height: 1.1;
	margin: 0;

	${ui.responsive.desktopFHD} {
		font-size: 88px;
	}
`

const Share = styled.span`
	color: ${colors.warmGrey};
	font-size: 32px;
	font-weight: 500;

	${ui.responsive.desktopFHD} {
		font-size: 44px;
	}
`

const Base = styled.p`
	color: ${colors.warmGrey};
	font-size: 16px;
	margin: 0;

	${ui.responsive.desktopFHD} {
		font-size: 20px;
	}
`

// Read by assistive tech, never shown: "3/4" on its own is a date to a screen reader
const VisuallyHidden = styled.span`
	height: 1px;
	overflow: hidden;
	position: absolute;
	width: 1px;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`

type CountProps = { count: number; total: number }

/** "3/4" in large type, "3 of 4" for assistive tech. */
const Count: FC<CountProps> = ({ count, total }) => (
	<>
		<span aria-hidden="true">
			{count}/{total}
		</span>
		<VisuallyHidden>
			<intl.Translate count={count} total={total}>
				knowledge_check.stage.summary.of
			</intl.Translate>
		</VisuallyHidden>
	</>
)

type Props = {
	summary: QuestionSummary
}

/**
 * How the room did on the question on screen: how many answered, and how
 * many got it right. A figure the review cannot work out for the question is
 * not in `summary`, and so not here (`questionSummary`).
 */
const QuestionSummaryPanel: FC<Props> = ({ summary }) => {
	const { participants, answered, correct } = summary
	return (
		<Stack>
			{answered || correct ? (
				<Figures>
					{answered ? (
						<div>
							<Caption>
								<intl.Translate>
									knowledge_check.stage.summary.answered
								</intl.Translate>
							</Caption>
							<Value>
								<Count count={answered.count} total={participants} />
							</Value>
						</div>
					) : null}
					{correct ? (
						<div>
							<Caption>
								<intl.Translate>
									knowledge_check.stage.summary.correct
								</intl.Translate>
							</Caption>
							<Value>
								<Count count={correct.count} total={participants} />
								<Share>{correct.percent}%</Share>
							</Value>
						</div>
					) : null}
				</Figures>
			) : null}
			<Base>
				<intl.Translate total={participants}>
					knowledge_check.stage.summary.base
				</intl.Translate>
			</Base>
		</Stack>
	)
}

export default QuestionSummaryPanel
